import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import countryNames from '../data/countryNames.json';
import CountryCombobox from '../components/CountryCombobox';
import { looksLikeCompleteEmail, isLikelyPersonalEmail, isLikelyProfessionalEmail } from '../utils/emailValidation';

const GUEST_TEMPLATE = () => ({
  guestTitle: 'Mr.',
  firstName: '',
  lastName: '',
  guestEmail: '',
  personalEmail: '',
  guestPhone: '',
  profession: '',
  organization: '',
  guestDepartment: '',
  industry: '',
  address: '',
  linkedinId: '',
  country: '',
});

const DEPARTMENT_OPTIONS = [
  'Engineering & Technology',
  'Sciences (Pure & Applied)',
  'Medical & Health Sciences',
  'Management & Commerce',
  'Humanities & Social Sciences',
  'Law & Legal Studies',
  'Arts, Design & Media',
  'Education & Teacher Training',
  'Agriculture & Allied Sciences',
  'Architecture & Planning',
  'Pharmacy',
  'Languages & Linguistics',
  'Defence & Military Studies',
  'Sports & Physical Education',
  'Other',
];

const INDUSTRY_OPTIONS = [
  'Information Technology & Software',
  'Manufacturing & Engineering',
  'Healthcare & Pharmaceuticals',
  'Banking, Finance & Insurance',
  'Education & Research',
  'Government & Public Sector',
  'Agriculture & Food Processing',
  'Energy & Environment',
  'Media, Arts & Entertainment',
  'Construction & Real Estate',
  'Retail & E-Commerce',
  'Logistics & Supply Chain',
  'Consulting & Professional Services',
  'Social Sector & NGOs',
  'Tourism & Hospitality',
  'Defence & Aerospace',
  'Startups & Entrepreneurship',
  'Other',
];

function pickOrOther(value, options) {
  if (!value) return '';
  return options.includes(value) ? value : 'Other';
}

function linkedinOk(s) {
  const t = String(s || '').trim().toLowerCase();
  return t.length >= 8 && (t.includes('linkedin') || t.includes('lnkd.in'));
}

function composeGuestName(guest) {
  return [guest.guestTitle, guest.firstName, guest.lastName].filter(Boolean).join(' ').trim();
}

function normalizeOtherValue(value) {
  return value === '__other__' ? '' : value;
}

function normalizeGuestForValidation(guest) {
  return {
    ...guest,
    guestDepartment: normalizeOtherValue(guest.guestDepartment),
    industry: normalizeOtherValue(guest.industry),
  };
}

function validateGuestsFront(list) {
  for (let i = 0; i < list.length; i++) {
    const g = list[i];
    const n = i + 1;
    const phone = String(g.guestPhone || '').replace(/\D/g, '');
    if (!g.guestTitle?.trim()) return `Guest ${n}: Title required`;
    if (!g.firstName?.trim()) return `Guest ${n}: First name required`;
    if (!looksLikeCompleteEmail(g.guestEmail)) return `Guest ${n}: Enter a valid work email`;
    if (!looksLikeCompleteEmail(g.personalEmail)) return `Guest ${n}: Enter a valid personal email`;
    if (String(g.guestEmail).trim().toLowerCase() === String(g.personalEmail).trim().toLowerCase()) return `Guest ${n}: Work and personal emails must differ`;

    if (isLikelyPersonalEmail(g.guestEmail)) return `Guest ${n}: Put your organization/work email here — ${g.guestEmail} looks personal`;
    if (isLikelyProfessionalEmail(g.personalEmail) && !isLikelyPersonalEmail(g.personalEmail)) {
      return `Guest ${n}: Personal email should be a typical personal inbox (Gmail, Yahoo, etc.), not ${g.personalEmail}`;
    }

    if (phone.length < 10 || phone.length > 15) return `Guest ${n}: Mobile must be numeric only (10–15 digits)`;
    if (!g.country || !countryNames.includes(g.country)) return `Guest ${n}: Choose country from suggestions (exact name)`;
    if (!g.profession?.trim()) return `Guest ${n}: Profession required`;
    if (!g.guestDepartment?.trim()) return `Guest ${n}: Department required`;
    if (!g.industry?.trim()) return `Guest ${n}: Industry required`;
    if (!g.address?.trim()) return `Guest ${n}: Address required`;
    if (!linkedinOk(g.linkedinId)) return `Guest ${n}: LinkedIn URL required (include linkedin.com or lnkd.in)`;
  }
  return null;
}

function downloadPdfBlob(blob, filename = 'visitor-pass.pdf') {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function fileSafeName(value, fallback) {
  const clean = String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  return clean || fallback;
}

function inputWarnStyle(show) {
  return show ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(220,38,38,0.14)' } : undefined;
}

function inputInfoStyle(show) {
  return show ? { borderColor: 'var(--amber)', boxShadow: '0 0 0 3px rgba(217,119,6,0.15)' } : undefined;
}

function professionalEmailLiveError(email) {
  const value = String(email || '').trim();
  if (!value) return '';
  if (!looksLikeCompleteEmail(value)) return 'Please enter organization email in valid format (e.g., name@company.com).';
  if (isLikelyPersonalEmail(value)) return 'Please enter organization email here. Personal email should go in the personal email field.';
  return '';
}

function personalEmailLiveError(email) {
  const value = String(email || '').trim();
  if (!value) return '';
  if (!looksLikeCompleteEmail(value)) return 'Please enter personal email in valid format (e.g., name@gmail.com).';
  if (isLikelyProfessionalEmail(value) && !isLikelyPersonalEmail(value)) {
    return 'Please enter personal email here. Organization email should go in the work email field.';
  }
  return '';
}

function IssuePassModal({ events, onClose, onIssued, initialEventId = '' }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(1);
  const [eventId, setEventId] = useState(initialEventId);
  const [sendEmail, setSendEmail] = useState(true);
  const [guests, setGuests] = useState([GUEST_TEMPLATE()]);

  React.useEffect(() => {
    setEventId(initialEventId);
    if (initialEventId) {
      setStep(2);
    }
  }, [initialEventId]);

  const selEvent = events.find((e) => e._id === eventId);
  const liveValidationError =
    step === 3
      ? validateGuestsFront(
          guests.map((g) =>
            normalizeGuestForValidation({
              ...g,
              guestName: composeGuestName(g),
              guestPhone: String(g.guestPhone || '').replace(/\D/g, ''),
            }),
          ),
        )
      : null;

  const setGuest = (idx, key, val) => {
    setGuests((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: val };
      return next;
    });
  };

  const handleCountNext = () => {
    const n = Math.min(100, Math.max(1, parseInt(String(count), 10) || 1));
    const ev = events.find((e) => e._id === eventId);
    if (!ev) return toast.error('Select an event');
    if (ev.approvalStatus !== 'approved') return toast.error('Venue approval is required before issuing passes');
    const capLeft = (ev.maxGuests || 0) - (ev.passCount || 0);
    if (n > capLeft) return toast.error(`Only ${capLeft} pass slot(s) left for this event`);
    setCount(n);
    setGuests(Array.from({ length: n }, () => GUEST_TEMPLATE()));
    setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const prepared = guests.map((g) => ({
      ...normalizeGuestForValidation(g),
      guestName: composeGuestName(g),
      guestPhone: String(g.guestPhone || '').replace(/\D/g, ''),
    }));

    const err = validateGuestsFront(prepared);
    if (err) {
      toast.error(err);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/passes/bulk', { eventId, sendEmail, guests: prepared });
      toast.success(`${res.data.passes?.length || 0} pass(es) issued`);
      const createdPasses = Array.isArray(res.data.passes) ? res.data.passes : [];
      if (createdPasses.length > 0) {
        let failedDownloads = 0;
        for (const pass of createdPasses) {
          try {
            const passId = pass?._id;
            if (!passId) {
              failedDownloads += 1;
              continue;
            }
            const first = fileSafeName(pass.firstName, 'Guest');
            const last = fileSafeName(pass.lastName, 'Pass');
            const pdfRes = await api.get(`/passes/export/pdf/${passId}`, {
              responseType: 'blob',
            });
            downloadPdfBlob(pdfRes.data, `${first}_${last}.pdf`);
          } catch {
            failedDownloads += 1;
          }
        }
        if (failedDownloads === 0) {
          toast.success('PDFs downloaded');
        } else if (failedDownloads < createdPasses.length) {
          toast.error(`${failedDownloads} PDF(s) failed to download`);
        } else {
          toast.error('Passes created but PDF download failed');
        }
      }
      onIssued();
    } catch (err3) {
      toast.error(err3.response?.data?.message || 'Failed to issue passes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation" onClick={(ev) => ev.target === ev.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: '44rem' }}>
        <div className="modal-header">
          <div className="modal-title">Issue visitor passes</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="issue-steps">
          {['Event', 'How many', 'Guest details'].map((s, i) => (
            <div key={s} className="issue-step-col">
              <div className={`issue-step-bar ${step > i ? 'is-done' : ''}`} />
              <div className={`issue-step-label ${step > i ? 'is-done' : ''}`}>{s}</div>
            </div>
          ))}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {step === 1 && (
              <>
                <div className="field">
                  <label htmlFor="ip-event">Event *</label>
                  <select id="ip-event" required value={eventId} onChange={(ev) => setEventId(ev.target.value)}>
                    <option value="">Choose an event…</option>
                    {events.map((ev) => (
                      <option key={ev._id} value={ev._id}>
                        {ev.title} — {new Date(ev.date).toLocaleDateString('en-IN')}
                      </option>
                    ))}
                  </select>
                </div>
                {selEvent && (
                  <div className="section-card" style={{ padding: '1rem 1.125rem', background: 'var(--gray-50)' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--text-sm)', color: 'var(--text-1)', marginBottom: '0.375rem' }}>
                      {selEvent.title}
                    </div>
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)' }}>
                      {selEvent.venue} · {new Date(selEvent.date).toLocaleDateString('en-IN', { dateStyle: 'long' })}
                    </div>
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', marginTop: '0.25rem' }}>
                      {selEvent.passCount || 0} / {selEvent.maxGuests} passes issued
                    </div>
                  </div>
                )}
              </>
            )}

            {step === 2 && (
              <>
                <div className="field">
                  <label htmlFor="ip-n">Number of passes *</label>
                  <input id="ip-n" type="number" min={1} max={100} value={count} onChange={(ev) => setCount(+ev.target.value)} />
                </div>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', lineHeight: 1.5 }}>
                  Next: for each guest you will collect <strong>work email</strong> (organization — not Gmail), <strong>personal email</strong> (e.g.
                  Gmail), <strong>full mobile number</strong> (digits only), <strong>department</strong>, <strong>industry</strong>, <strong>address</strong>, mandatory <strong>LinkedIn</strong>, and country via search.
                  Fields highlight in red if work/personal emails are swapped.
                </p>
              </>
            )}

            {step === 3 && (
              <>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', marginBottom: '0.75rem' }}>
                  {guests.length} guest profile(s).
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '50vh', overflowY: 'auto' }}>
                  {guests.map((g, i) => {
                    const profEmailError = professionalEmailLiveError(g.guestEmail);
                    const personalEmailError = personalEmailLiveError(g.personalEmail);

                    const liWarn = g.linkedinId && String(g.linkedinId).trim() && !linkedinOk(g.linkedinId);
                    const deptPick = pickOrOther(g.guestDepartment, DEPARTMENT_OPTIONS);
                    const industryPick = pickOrOther(g.industry, INDUSTRY_OPTIONS);

                    return (
                      <div key={i} className="section-card" style={{ padding: '1rem', background: 'var(--gray-50)' }}>
                        <div style={{ fontWeight: 800, fontSize: 'var(--text-xs)', marginBottom: '0.5rem', color: 'var(--navy)' }}>Guest {i + 1}</div>
                        <div className="grid-2">
                          <div className="field">
                            <label>Title *</label>
                            <select required value={g.guestTitle} onChange={(ev) => setGuest(i, 'guestTitle', ev.target.value)}>
                              {['Mr.', 'Ms.', 'Mrs.', 'Dr.', 'Prof.', 'Mx.'].map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="field">
                            <label>First name *</label>
                            <input required value={g.firstName} onChange={(ev) => setGuest(i, 'firstName', ev.target.value)} placeholder="First name" />
                          </div>
                        </div>
                        <div className="field">
                          <label>Last name</label>
                          <input
                            value={g.lastName}
                            onChange={(ev) => setGuest(i, 'lastName', ev.target.value)}
                            placeholder="Last name"
                          />
                        </div>
                        <div className="field">
                          <label>Work / professional email *</label>
                          <input
                            required
                            type="email"
                            autoComplete="off"
                            placeholder="you@company.com"
                            style={inputWarnStyle(Boolean(profEmailError))}
                            value={g.guestEmail}
                            onChange={(ev) => setGuest(i, 'guestEmail', ev.target.value)}
                          />
                          {profEmailError && <span className="field-error-msg">{profEmailError}</span>}
                        </div>
                        <div className="grid-2">
                          <div className="field">
                            <label>Personal email *</label>
                            <input
                              required
                              type="email"
                              autoComplete="off"
                              placeholder="you@gmail.com"
                              style={inputInfoStyle(Boolean(personalEmailError))}
                              value={g.personalEmail}
                              onChange={(ev) => setGuest(i, 'personalEmail', ev.target.value)}
                            />
                            {personalEmailError && <span className="field-error-msg">{personalEmailError}</span>}
                          </div>
                          <div className="field">
                            <label>Mobile * (digits only)</label>
                            <input
                              required
                              inputMode="numeric"
                              pattern="[0-9]*"
                              placeholder="e.g. 919876543210"
                              maxLength={15}
                              value={g.guestPhone}
                              onChange={(ev) => setGuest(i, 'guestPhone', ev.target.value.replace(/\D/g, '').slice(0, 15))}
                            />
                           </div>
                        </div>
                        <CountryCombobox
                          id={`guest-${i}-country`}
                          label="Country"
                          required
                          value={g.country}
                          onChange={(val) => setGuest(i, 'country', val)}
                        />
                        <div className="grid-2">
                          <div className="field">
                            <label>Designation *</label>
                            <input required value={g.profession} onChange={(ev) => setGuest(i, 'profession', ev.target.value)} />
                          </div>
                          <div className="field">
                            <label>Organization</label>
                            <input value={g.organization} onChange={(ev) => setGuest(i, 'organization', ev.target.value)} />
                          </div>
                        </div>
                        <div className="grid-2">
                          <div className="field">
                            <label>Department *</label>
                            <select
                              required
                              value={deptPick}
                              onChange={(ev) => setGuest(i, 'guestDepartment', ev.target.value === 'Other' ? '__other__' : ev.target.value)}
                            >
                              <option value="">Select department...</option>
                              {DEPARTMENT_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                            {deptPick === 'Other' && (
                              <input
                                required
                                value={g.guestDepartment === '__other__' ? '' : g.guestDepartment}
                                onChange={(ev) => setGuest(i, 'guestDepartment', ev.target.value)}
                                placeholder="Enter department"
                                style={{ marginTop: '0.5rem' }}
                              />
                            )}
                          </div>
                          <div className="field">
                            <label>Industry *</label>
                            <select
                              required
                              value={industryPick}
                              onChange={(ev) => setGuest(i, 'industry', ev.target.value === 'Other' ? '__other__' : ev.target.value)}
                            >
                              <option value="">Select industry...</option>
                              {INDUSTRY_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                            {industryPick === 'Other' && (
                              <input
                                required
                                value={g.industry === '__other__' ? '' : g.industry}
                                onChange={(ev) => setGuest(i, 'industry', ev.target.value)}
                                placeholder="Enter industry"
                                style={{ marginTop: '0.5rem' }}
                              />
                            )}
                          </div>
                        </div>
                        <div className="field">
                          <label>Address *</label>
                          <textarea
                            required
                            rows={3}
                            placeholder="Street, city, state, postal code"
                            value={g.address}
                            onChange={(ev) => setGuest(i, 'address', ev.target.value)}
                          />
                        </div>
                        <div className="field">
                          <label>LinkedIn *</label>
                          <input
                            required
                            placeholder="https://linkedin.com/in/username"
                            style={inputWarnStyle(liWarn)}
                            value={g.linkedinId}
                            onChange={(ev) => setGuest(i, 'linkedinId', ev.target.value)}
                          />
                          {liWarn && <span className="field-error-msg">Must include linkedin.com or lnkd.in</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <label className="check-row">
                  <input type="checkbox" checked={sendEmail} onChange={(ev) => setSendEmail(ev.target.checked)} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>Email pass to guest (work email)</div>
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)' }}>Pass PDF is emailed to the professional inbox.</div>
                  </div>
                </label>
              </>
            )}
          </div>
          <div className="modal-footer">
            {step > 1 && (
              <button type="button" className="btn btn-ghost" onClick={() => setStep((s) => s - 1)}>
                ← Back
              </button>
            )}
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            {step === 1 && (
              <button
                type="button"
                className="btn btn-navy"
                onClick={() => {
                  if (!eventId) return toast.error('Select an event');
                  const ev = events.find((e) => e._id === eventId);
                  if (ev?.approvalStatus !== 'approved') return toast.error('Venue approval is required before issuing passes');
                  setStep(2);
                }}
              >
                Next
              </button>
            )}
            {step === 2 && (
              <button type="button" className="btn btn-navy" onClick={handleCountNext}>
                Next
              </button>
            )}
            {step === 3 && (
              <>
                {liveValidationError && (
                  <span className="field-error-msg" style={{ marginRight: '0.5rem' }}>
                    {liveValidationError}
                  </span>
                )}
                <button
                  type="submit"
                  className="btn btn-navy"
                  disabled={loading || Boolean(liveValidationError)}
                  style={{ opacity: loading || liveValidationError ? 0.5 : 1 }}
                >
                  {loading ? (
                    <>
                      <span className="spinner" /> Issuing…
                    </>
                  ) : (
                    'Issue & download PDF'
                  )}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export { IssuePassModal };

function PassDetailModal({ pass, onClose, onResend }) {
  const displayName = pass.guestName || composeGuestName(pass) || '—';
  const downloadPass = () => {
    const w = window.open('', '_blank');
    const html = `<!DOCTYPE html><html><head><title>Visitor Pass ${pass.passCode}</title>
<style>
body{font-family:system-ui,sans-serif;max-width:520px;margin:40px auto;padding:0 20px;line-height:1.5;color:#0f172a;}
.card{border:2px solid #115e59;border-radius:14px;overflow:hidden;}
.header{background:linear-gradient(145deg,#115e59,#0d9488);color:white;padding:22px;text-align:center;}
.code{font-size:1.35rem;font-weight:800;letter-spacing:.12em;color:#115e59;text-align:center;font-family:ui-monospace,monospace;padding:16px;background:#ecfdf9;border-radius:10px;margin:16px 0;}
.row{display:flex;justify-content:space-between;gap:12px;padding:10px 0;border-bottom:1px solid #e2e8f0;font-size:14px;}
.label{color:#64748b;font-weight:600;}
.validity{background:#d1fae5;padding:12px;border-radius:10px;text-align:center;font-size:13px;color:#065f46;}
</style></head><body>
<div class="card">
<div class="header"><h2 style="margin:0;font-size:1rem;letter-spacing:.15em;">VISITOR PASS</h2></div>
<div style="padding:22px;">
<div class="code">${pass.passCode}</div>
<div class="qr" style="text-align:center"><img src="${pass.qrCodeData}" width="140" height="140" alt="QR" /></div>
<div class="row"><span class="label">Title</span><span>${pass.guestTitle || '—'}</span></div>
<div class="row"><span class="label">First name</span><span>${pass.firstName || '—'}</span></div>
<div class="row"><span class="label">Last name</span><span>${pass.lastName || '—'}</span></div>
<div class="row"><span class="label">Guest</span><span>${displayName}</span></div>
<div class="row"><span class="label">Work email</span><span>${pass.guestEmail}</span></div>
<div class="row"><span class="label">Personal email</span><span>${pass.personalEmail || '—'}</span></div>
<div class="row"><span class="label">Mobile</span><span>${pass.guestPhone || '—'}</span></div>
<div class="row"><span class="label">LinkedIn</span><span style="word-break:break-all">${pass.linkedinId || '—'}</span></div>
<div class="row"><span class="label">Country</span><span>${pass.country || '—'}</span></div>
<div class="row"><span class="label">Profession</span><span>${pass.profession}</span></div>
<div class="row"><span class="label">Department</span><span>${pass.guestDepartment || '—'}</span></div>
<div class="row"><span class="label">Industry</span><span>${pass.industry || '—'}</span></div>
<div class="row"><span class="label">Address</span><span>${pass.address || '—'}</span></div>
<div class="row"><span class="label">Event</span><span>${pass.event?.title}</span></div>
<div class="row"><span class="label">Venue</span><span>${pass.event?.venue}</span></div>
<div class="row"><span class="label">Date</span><span>${pass.event?.date ? new Date(pass.event.date).toLocaleDateString('en-IN') : ''}</span></div>
<div class="validity">Valid on event date. Present at gate with ID.</div>
</div></div></body></html>`;
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  const rows = [
    ['Title', pass.guestTitle || '—'],
    ['First name', pass.firstName || '—'],
    ['Last name', pass.lastName || '—'],
    ['Guest name', pass.guestName || displayName],
    ['Country', pass.country],
    ['Work email', pass.guestEmail],
    ['Personal email', pass.personalEmail],
    ['Mobile', pass.guestPhone || '—'],
    ['LinkedIn', pass.linkedinId],
    ['Profession', pass.profession],
    ['Department', pass.guestDepartment || '—'],
    ['Industry', pass.industry || '—'],
    ['Address', pass.address || '—'],
    ['Organization', pass.organization || 'N/A'],
  ];

  return (
    <div className="modal-overlay" role="presentation" onClick={(ev) => ev.target === ev.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: '32.5rem' }}>
        <div className="modal-header">
          <div className="modal-title">Pass details</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="modal-body">
          <div className="pass-hero-block">
            <div className="pass-hero-label">Entry code</div>
            <div className="pass-hero-code">{pass.passCode}</div>
            {pass.qrCodeData && (
              <img src={pass.qrCodeData} alt="QR" style={{ width: 128, height: 128, background: '#fff', padding: 8, borderRadius: 12, margin: '0 auto' }} />
            )}
          </div>
          <span className={`badge badge-${pass.status}`} style={{ marginLeft: 'auto', display: 'block', width: 'fit-content' }}>
            {pass.status}
          </span>
          {rows.map(([l, v]) => (
            <div key={l} className="pass-detail-row">
              <span className="pass-detail-label">{l}</span>
              <span className="pass-detail-value">{v}</span>
            </div>
          ))}
          {pass.entryTime && (
            <div style={{ background: 'var(--green-light)', borderRadius: 'var(--radius)', padding: '0.625rem 0.875rem', fontSize: 'var(--text-sm)', color: '#065f46', border: '1px solid #a7f3d0' }}>
              Entered: {new Date(pass.entryTime).toLocaleString('en-IN')}
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={downloadPass}>
            Print view
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onResend(pass._id)}>
            Resend email
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Passes() {
  const [passes, setPasses] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIssue, setShowIssue] = useState(false);
  const [viewPass, setViewPass] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEvent, setFilterEvent] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/passes'), api.get('/events')])
      .then(([pr, er]) => {
        setPasses(pr.data.passes || []);
        setEvents(er.data.events || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = passes.filter((p) => {
    if (filterStatus && p.status !== filterStatus) return false;
    if (filterEvent && p.event?._id !== filterEvent) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        p.guestName?.toLowerCase().includes(s) ||
        p.guestEmail?.toLowerCase().includes(s) ||
        p.personalEmail?.toLowerCase().includes(s) ||
        p.passCode?.toLowerCase().includes(s) ||
        p.country?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const handleIssued = () => {
    setShowIssue(false);
    load();
  };

  const handleResend = async (id) => {
    try {
      await api.post(`/passes/${id}/resend`);
      toast.success('Email sent!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Email failed');
    }
  };

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">Guest details</div>
          <div className="page-subtitle">{passes.length} total issued</div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-navy" onClick={() => setShowIssue(true)}>
            Issue passes
          </button>
        </div>
      </div>

      <div className="page-body">
        <div className="card toolbar" style={{ marginBottom: '1rem' }}>
          <select className="toolbar-select" value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)} style={{ maxWidth: '13.75rem' }}>
            <option value="">All events</option>
            {events.map((ev) => (
              <option key={ev._id} value={ev._id}>
                {ev.title}
              </option>
            ))}
          </select>
          <input className="toolbar-input" placeholder="Search name, emails, code, country" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        {loading ? (
          <div className="page-loading">
            <div className="spinner" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-icon">Nothing to show</div>
              <div className="empty-title">{passes.length === 0 ? 'No passes yet' : 'No matches'}</div>
              {passes.length === 0 && (
                <button type="button" className="btn btn-navy" style={{ margin: '1rem auto 0', display: 'flex' }} onClick={() => setShowIssue(true)}>
                  Issue passes
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Guest</th>
                  <th>Code</th>
                  <th>Event</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th>Email</th>
                  <th>Issued</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p._id}>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer' }}>
                      <div className="cell-stack-title">{p.guestName}</div>
                      <div className="cell-stack-muted">{[p.guestTitle, p.firstName, p.lastName].filter(Boolean).join(' ')}</div>
                      <div className="cell-stack-muted">{p.guestEmail}</div>
                      {p.personalEmail && <div className="cell-stack-muted">{p.personalEmail}</div>}
                    </td>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer' }}>
                      <span className="code-pill-table">{p.passCode}</span>
                    </td>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer' }}>
                      <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--text-2)' }}>{p.event?.title}</div>
                      <div className="cell-stack-muted">{p.event?.date ? new Date(p.event.date).toLocaleDateString('en-IN') : ''}</div>
                    </td>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer' }}>
                      {p.country || '—'}
                    </td>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer' }}>
                      <span className={`badge badge-${p.status}`}>{p.status}</span>
                    </td>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer' }}>
                      {p.emailSent ? (
                        <span style={{ color: 'var(--green)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>Sent</span>
                      ) : (
                        <span style={{ color: 'var(--gray-400)', fontSize: 'var(--text-sm)' }}>Pending</span>
                      )}
                    </td>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer', fontSize: 'var(--text-sm)', color: 'var(--text-3)' }}>
                      {new Date(p.createdAt).toLocaleDateString('en-IN')}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setViewPass(p)}>
                          View
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => handleResend(p._id)}>
                          Resend
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showIssue && <IssuePassModal events={events} onClose={() => setShowIssue(false)} onIssued={handleIssued} />}
      {viewPass && <PassDetailModal pass={viewPass} onClose={() => setViewPass(null)} onResend={handleResend} />}
    </div>
  );
}
